import React, { useEffect, useState } from "react";
import TextField from "@/components/ui/Textfield";
import Button from "@/components/ui/Button";
import Layout from "@/components/layout/Layout";
import Checkbox from "@/components/ui/CheckBox";
import useGlobalStore from "@/store/globalStore";
import useAuthStore, { User } from "@/store/authStore";
import { useShallow } from "zustand/react/shallow";
import useProfileStore, { Passwords } from "./store/profileStore";
import useNotificationStore from "./store/notificationStore";
import NotificationPreferencesPanel from "./components/notification-preferences";

interface Project {
  id: string;
  code: string;
  name: string;
  company: string;
  icon: string;
  notifications: boolean;
}

interface NotificationPreferences {
  emailNotifications: boolean;
  showUnreadCount: boolean;
}

const ProfileComponent: React.FC = () => {
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    bio: "",
    email: "",
    id: "",
    old_password: "",
    new_password: "",
    confirm_password: "",
    emailNotifications: false,
    showUnreadCount: true,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  const [projects, setProjects] = useState<Project[]>([
    {
      id: "1",
      code: "KUS-0012",
      name: "Q1 Spring Campaign",
      company: "Kusmi Tea",
      icon: "🍵",
      notifications: true,
    },
    {
      id: "2",
      code: "KUS-0013",
      name: "Q1 Spring Campaign",
      company: "Kusmi Tea",
      icon: "🍵",
      notifications: true,
    },
    {
      id: "3",
      code: "MCD-0012",
      name: "Q1 Spring Campaign",
      company: "McDonald's",
      icon: "🍟",
      notifications: true,
    },
    {
      id: "4",
      code: "MCD-0013",
      name: "Q1 Spring Campaign",
      company: "McDonald's",
      icon: "🍟",
      notifications: true,
    },
  ]);

  const showError = useGlobalStore((state) => state.showError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);
  const editProfile = useProfileStore((state) => state.editProfile);
  const updateProfilePicture = useProfileStore((state) => state.updateProfilePicture);
  const {
    preferences,
    getPreferences,
    setPreferences,
  } = useNotificationStore();

  const [projectToggles, setProjectToggles] = useState<Record<string, boolean>>(
    projects.reduce((acc, project) => {
      acc[project.id] = project.notifications;
      return acc;
    }, {} as Record<string, boolean>)
  );

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showError("Please select a valid image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showError("Image size should be less than 5MB");
        return;
      }

      setSelectedImageFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setProfileImage(imageUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerImageUpload = () => {
    const fileInput = document.getElementById(
      "profile-image-upload"
    ) as HTMLInputElement;
    fileInput?.click();
  };

  const handleProfileChange =
    (field: keyof User | keyof Passwords) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setProfile((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleNotificationPrefChange = (
    field: keyof NotificationPreferences
  ) => {
    const newValue = !profile[field];

    setProfile((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    // Update preferences in the store and localStorage
    const updatedPreferences = {
      ...preferences,
      emailNotifications:
        field === "emailNotifications"
          ? newValue
          : profile.emailNotifications,
      showUnreadCount:
        field === "showUnreadCount" ? newValue : profile.showUnreadCount,
    };

    setPreferences(updatedPreferences);
    showSuccess("Notification preference updated!");
  };

  const handleProjectNotificationToggle = (projectId: string) => {
    setProjectToggles((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));

    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId
          ? { ...project, notifications: !project.notifications }
          : project
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      first_name,
      last_name,
      bio,
      email,
      id,
      old_password,
      new_password,
      confirm_password,
    } = profile;

    if (!first_name.trim()) return showError("First name is required.");
    if (!last_name.trim()) return showError("Last Name is required.");
    if (!email.trim()) return showError("Email is required.");

    const isChangingPassword =
      old_password.trim() || new_password.trim() || confirm_password.trim();

    if (isChangingPassword) {
      if (!old_password.trim()) return showError("Old password is required.");
      if (!new_password.trim()) return showError("New password is required.");
      if (!confirm_password.trim())
        return showError("Confirm password is required.");
      if (new_password !== confirm_password)
        return showError("Passwords do not match.");
    }

    const payload: any = {
      first_name,
      last_name,
      bio,
      email,
      id,
    };

    if (old_password && new_password && confirm_password) {
      payload.old_password = old_password;
      payload.new_password = new_password;
      payload.new_password_confirmation = confirm_password;
    }

    try {
      setIsLoading(true);
      if (selectedImageFile) {
        await updateProfilePicture(selectedImageFile);
        setSelectedImageFile(null);
      }
      await editProfile(payload);
      showSuccess("Profile updated successfully!");
    } catch (err: any) {
      showError(err?.message || "Failed to submit user data.");
    } finally {
      setIsLoading(false);
    }
  };

  const groupedProjects = projects.reduce((acc, project) => {
    if (!acc[project.company]) {
      acc[project.company] = [];
    }
    acc[project.company].push(project);
    return acc;
  }, {} as Record<string, Project[]>);

  const { user, user_role } = useAuthStore(
    useShallow((state) => ({
      user: state.user,
      user_role: state.user_role,
    }))
  );

  useEffect(() => {
    setProfile({
      ...profile,
      first_name: user.first_name,
      last_name: user.last_name,
      bio: user.bio,
      email: user.email,
      id: user.id,
    });
    if (user.profile_picture_url_signed && !selectedImageFile) {
      setProfileImage(user.profile_picture_url_signed);
    }
  }, [user]);

  // Fetch notification preferences on component mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        await getPreferences();
      } catch (error) {
        // Check if there are saved preferences in localStorage
        const savedPreferences = localStorage.getItem("notificationPreferences");
        if (savedPreferences) {
          const parsed = JSON.parse(savedPreferences);
          setPreferences(parsed);
        }
      }
    };

    fetchPreferences();
  }, []);

  // Update profile state when preferences are loaded
  useEffect(() => {
    if (preferences) {
      console.log("Preferences loaded in profile:", preferences);
      console.log("Groups:", preferences.groups);
      console.log("Groups length:", preferences.groups?.length);

      setProfile((prev) => ({
        ...prev,
        emailNotifications: preferences.emailNotifications ?? prev.emailNotifications,
        showUnreadCount: preferences.showUnreadCount ?? prev.showUnreadCount,
      }));
    }
  }, [preferences]);

  return (
    <Layout>
      <div>
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          </div>

          <div>
            <div className="flex justify-between pb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Personal Information
              </h2>
              <button className="text-sm text-gray-500 hover:text-gray-700 underline">
                Delete my account
              </button>
            </div>

            <div className="border-1 border-b border-[#C72BA4] mb-5"></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    First Name
                  </label>
                  <TextField
                    type="text"
                    value={profile.first_name}
                    onChange={handleProfileChange("first_name")}
                    className="flex-1"
                    placeholder="Enter Your First Name"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Last Name
                  </label>
                  <TextField
                    type="text"
                    value={profile.last_name}
                    onChange={handleProfileChange("last_name")}
                    className="flex-1"
                    placeholder="Enter Your Last Name"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Email
                  </label>
                  <TextField
                    type="email"
                    value={profile.email}
                    onChange={handleProfileChange("email")}
                    placeholder="Enter your email address"
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Bio
                  </label>
                  <TextField
                    type="text"
                    value={profile.bio}
                    onChange={handleProfileChange("bio")}
                    placeholder="Enter your Bio"
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Role
                  </label>
                  <TextField
                    type="text"
                    value={user_role}
                    className="flex-1"
                    disabled={true}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Old Password
                  </label>
                  <TextField
                    type="password"
                    value={profile.old_password}
                    onChange={handleProfileChange("old_password")}
                    placeholder="Enter your Old password"
                    className="flex-1"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                      New Password
                    </label>
                    <TextField
                      type="password"
                      value={profile.new_password}
                      onChange={handleProfileChange("new_password")}
                      placeholder="Enter your New password"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                      Confirm Password
                    </label>
                    <TextField
                      type="password"
                      value={profile.confirm_password}
                      onChange={handleProfileChange("confirm_password")}
                      placeholder="Enter your password"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="border rounded-xl p-6 flex">
                  <div className="text-center">
                    <div className="w-24 h-24 bg-[#EA33C0] rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto mb-4 overflow-hidden relative">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        (profile.first_name?.[0] ?? "").toUpperCase()
                      )}
                    </div>
                    <input
                      id="profile-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <button
                      onClick={triggerImageUpload}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Edit Image
                    </button>
                  </div>
                  <div className="ml-10 mt-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {profile.first_name}
                    </h3>
                    <p className="text-gray-600 mb-4">{user_role}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Notification Preferences
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <Checkbox
                        checked={profile.emailNotifications}
                        onChange={() =>
                          handleNotificationPrefChange("emailNotifications")
                        }
                      />
                      <span className="ml-3 text-gray-700">
                        Send me email notifications
                      </span>
                    </label>
                    <label className="flex items-center">
                      <Checkbox
                        checked={profile.showUnreadCount}
                        onChange={() =>
                          handleNotificationPrefChange("showUnreadCount")
                        }
                      />
                      <span className="ml-3 text-gray-700">
                        Show me the number of unread items in my MediaBridge
                        account
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Button
                type="primary"
                text="Save Changes"
                onClick={handleSubmit}
                className="px-8"
                isLoading={isLoading}
              />
            </div>
          </div>

          <div className="mt-10">
            <div className="flex justify-between items-center pb-3">
              <h2 className="text-xl font-semibold text-gray-900">
                Manage Notification Preferences
              </h2>
            </div>

            <div className="border-1 border-b border-[#C72BA4] mb-5"></div>

            <p className="text-gray-600 mb-6">
              Choose what MediaBridge tells you about. Each switch covers the activity described under it.
            </p>

            <NotificationPreferencesPanel />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfileComponent;
